import React, { useState, useRef, useEffect } from 'react';
import { lbl, inp } from './shared';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY',
];

async function searchAddress(query) {
  const res = await fetch(`/api/address-search?q=${encodeURIComponent(query)}`);
  if (!res.ok) return [];
  return res.json();
}

function formatPhone(val) {
  const digits = val.replace(/\D/g, '').slice(0, 10);
  if (digits.length < 4) return digits;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function AddressAutocomplete({ label, value, prefix, onChange, hint, showCounty }) {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setSuggestions([]);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  function handleType(val) {
    setQuery(val);
    onChange(`${prefix}address`, val);
    clearTimeout(debounceRef.current);
    if (val.length < 4) { setSuggestions([]); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const results = await searchAddress(val);
        setSuggestions(results);
      } catch {
        setSuggestions([]);
      }
      setLoading(false);
    }, 350);
  }

  function handleSelect(s) {
    setQuery(s.street || s.display);
    setSuggestions([]);
    onChange(`${prefix}address`, s.street || s.display);
    if (s.city)   onChange(`${prefix}city`, s.city);
    if (s.state)  onChange(`${prefix}state`, s.state);
    if (s.zip)    onChange(`${prefix}zip`, s.zip);
    if (showCounty && s.county) onChange('county', s.county);
  }

  return (
    <div style={{ marginBottom: 18 }} ref={containerRef}>
      <label style={lbl}>
        {label}
        {loading && <span style={{ fontWeight: 400, color: '#7a99b8', marginLeft: 8, textTransform: 'none', letterSpacing: 0 }}>Searching…</span>}
      </label>
      <div style={{ position: 'relative' }}>
        <input
          style={inp}
          name={`${prefix || 'physical'}-street-${label.replace(/\s+/g, '-').toLowerCase()}`}
          value={query}
          onChange={e => handleType(e.target.value)}
          placeholder="Start typing your address…"
          autoComplete="new-password"
          autoCorrect="off"
          spellCheck={false}
        />
        {suggestions.length > 0 && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0,
            background: '#fff', border: '1.5px solid #c5d5e8', borderRadius: 8,
            zIndex: 200, maxHeight: 220, overflowY: 'auto',
            boxShadow: '0 6px 16px rgba(26,51,100,0.13)',
            marginTop: 2,
          }}>
            {suggestions.map((s, i) => (
              <div
                key={i}
                onMouseDown={() => handleSelect(s)}
                style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 13, color: '#1a3364', borderBottom: i < suggestions.length - 1 ? '1px solid #eef2f7' : 'none' }}
                onMouseEnter={e => e.currentTarget.style.background = '#dbeef8'}
                onMouseLeave={e => e.currentTarget.style.background = '#fff'}
              >
                <div style={{ fontWeight: 600 }}>{s.street || s.display.split(',')[0]}</div>
                <div style={{ color: '#7a99b8', fontSize: 12, marginTop: 2 }}>
                  {[s.city, s.state, s.zip].filter(Boolean).join(', ')}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {hint && (
        <p style={{ fontSize: 12, color: '#7a99b8', margin: '5px 0 0' }}>{hint}</p>
      )}
    </div>
  );
}

function ContactRecap({ data, onEdit }) {
  const fullAddress = [data.address, data.address2, data.city, data.state, data.zip].filter(Boolean).join(', ');
  const mailingIsSame = data.mailingIsSameAsPhysical !== false;
  const fullMailingAddress = mailingIsSame ? null : [data.mailingAddress, data.mailingAddress2, data.mailingCity, data.mailingState, data.mailingZip].filter(Boolean).join(', ');

  return (
    <div>
      <p style={{ fontSize: 14, color: '#4a6288', marginTop: 0, marginBottom: 18, lineHeight: 1.5 }}>
        This is the contact information we have on file. If it's still correct, click <strong>Next</strong> below to continue. If anything needs to change, click <strong>Update</strong>.
      </p>
      <div style={{ background: '#f4f8fc', border: '1.5px solid #c5d5e8', borderRadius: 10, padding: '18px 20px', marginBottom: 18, maxWidth: 360 }}>
        {[
          ['Address', fullAddress],
          ...(fullMailingAddress ? [['Mailing Address', fullMailingAddress]] : []),
          ['Email', data.email],
          ['Phone', data.phone],
        ].map(([label, value]) => (
          <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 14 }}>
            <span style={{ color: '#7a99b8', flex: 1 }}>{label}</span>
            <span style={{ color: '#1a3364', fontWeight: 600, flex: 2, textAlign: 'right' }}>{value || '—'}</span>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={onEdit}
        style={{ padding: '12px 24px', background: 'transparent', border: '2px solid #1a3364', borderRadius: 8, color: '#1a3364', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}
      >
        Update
      </button>
    </div>
  );
}

export default function ContactInfo({ data, onChange, mode }) {
  const mailingIsSame = data.mailingIsSameAsPhysical !== false;
  const hasAddressOnFile = Boolean(String(data.address || '').trim());
  const [editing, setEditing] = useState(mode !== 'verify' || !hasAddressOnFile);

  if (mode === 'verify' && !editing) {
    return <ContactRecap data={data} onEdit={() => setEditing(true)} />;
  }

  return (
    <div>
      {/* Name */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 2fr', gap: 14, marginBottom: 18 }}>
        <div>
          <label style={lbl}>First Name</label>
          <input data-testid="firstName" style={inp} value={data.firstName} onChange={e => onChange('firstName', e.target.value)} />
        </div>
        <div>
          <label style={lbl}>Middle Name</label>
          <input style={inp} value={data.middleName} onChange={e => onChange('middleName', e.target.value)} />
        </div>
        <div>
          <label style={lbl}>Last Name</label>
          <input data-testid="lastName" style={inp} value={data.lastName} onChange={e => onChange('lastName', e.target.value)} />
        </div>
      </div>

      {/* Physical Address */}
      <AddressAutocomplete
        label="Physical Address"
        value={data.address}
        prefix=""
        onChange={onChange}
        hint="Type your street address and select from suggestions to auto-fill city, state, and ZIP."
        showCounty
      />

      {/* Same Ste/Apt | City | State | ZIP row (and field names) as IndFam_Intake */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
        <div>
          <label style={lbl}>Ste/Apt</label>
          <input data-testid="address2" style={inp} value={data.address2 || ''} onChange={e => onChange('address2', e.target.value)} />
        </div>
        <div>
          <label style={lbl}>City</label>
          <input data-testid="city" style={inp} value={data.city} onChange={e => onChange('city', e.target.value)} />
        </div>
        <div>
          <label style={lbl}>State</label>
          <select data-testid="state" style={inp} value={data.state} onChange={e => onChange('state', e.target.value)}>
            <option value="">--</option>
            {US_STATES.map(st => <option key={st} value={st}>{st}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>ZIP</label>
          <input data-testid="zip" style={inp} maxLength={10} value={data.zip} onChange={e => onChange('zip', e.target.value)} />
        </div>
      </div>

      <div style={{ marginBottom: 18 }}>
        <label style={lbl}>County</label>
        <input data-testid="county" style={inp} value={data.county} onChange={e => onChange('county', e.target.value)} />
      </div>

      {/* Mailing address toggle */}
      <div style={{ marginBottom: 18 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
          <input
            type="checkbox"
            checked={mailingIsSame}
            onChange={e => onChange('mailingIsSameAsPhysical', e.target.checked)}
            style={{ width: 17, height: 17, cursor: 'pointer', accentColor: '#1a3364' }}
          />
          <span style={{ fontSize: 14, color: '#1a3364', fontWeight: 500 }}>
            My mailing address is the same as my physical address
          </span>
        </label>
      </div>

      {/* Mailing Address — shown only when different */}
      {!mailingIsSame && (
        <div style={{ background: '#f4f8fc', border: '1.5px solid #c5d5e8', borderRadius: 10, padding: '16px 18px', marginBottom: 18 }}>
          <AddressAutocomplete
            label="Mailing Address"
            value={data.mailingAddress}
            prefix="mailing"
            onChange={onChange}
            hint="For a P.O. Box, type it directly (e.g. PO Box 123) — no need to select a suggestion."
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr 1fr', gap: 14 }}>
            <div>
              <label style={lbl}>Ste/Apt</label>
              <input style={inp} value={data.mailingAddress2 || ''} onChange={e => onChange('mailingAddress2', e.target.value)} />
            </div>
            <div>
              <label style={lbl}>City</label>
              <input style={inp} value={data.mailingCity} onChange={e => onChange('mailingCity', e.target.value)} />
            </div>
            <div>
              <label style={lbl}>State</label>
              <select style={inp} value={data.mailingState} onChange={e => onChange('mailingState', e.target.value)}>
                <option value="">--</option>
                {US_STATES.map(st => <option key={st} value={st}>{st}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>ZIP</label>
              <input style={inp} maxLength={10} value={data.mailingZip} onChange={e => onChange('mailingZip', e.target.value)} />
            </div>
          </div>
        </div>
      )}

      <div style={{ marginBottom: 18 }}>
        <label style={lbl}>Email Address</label>
        <input style={inp} type="email" value={data.email} onChange={e => onChange('email', e.target.value)} />
      </div>

      <div style={{ marginBottom: 18 }}>
        <label style={lbl}>Phone Number</label>
        <input
          style={inp}
          type="tel"
          placeholder="(555) 000-0000"
          value={data.phone}
          onChange={e => onChange('phone', formatPhone(e.target.value))}
        />
      </div>
    </div>
  );
}
